<!-- eslint-disable unused-imports/no-unused-vars -->
<script setup lang="ts">
import { SolButton, SolEmptyState } from '@solfacil/girassol'
import validateFormalizationStep from '~/composables/formalization/useFormalizationStepGuard'
import env from '~/env'
import Solfacil from '~/pages/customer/solfacil/index.vue'
import useFinancing from '~/store-v2/financings/useFinancing'
import useHardwareForm from '~/store-v2/hardware/useHardwareForm'
import useProject from '~/store-v2/projects/useProject'
import useRegistration from '~/store-v2/registration/useRegistration'
import SimulationResumeWidget from '~/ui-blocks/SimulationResumeWidget/SimulationResumeWidget.vue'
import { getErrorMessage } from '~/utils/errors'

// base hooks
const router = useRouter()
const route = useRoute<any>()

// initial variables and computed
const projectId = computed(() => route.params.id as string)

// domain hooks
const {
  data: registration,
  error: errorRegistration,
  isError: isErrorRegistration,
  isLoading: isLoadingRegistration,
  refetch: refetchRegistration,
} = useRegistration(projectId)

const {
  data: project,
  isLoading: isLoadingProject,
  error: errorProject,
  isError: isErrorProject,
  refetch: refetchProject,
} = useProject(projectId)

const {
  data: financing,
  isLoading: isLoadingFinancing,
  isError: isErrorFinancing,
  error: errorFinancing,
  refetch: refetchFinancing,
} = useFinancing(projectId)

const {
  data: hardwareForm,
  isError: isErrorHardwareForm,
  isLoading: isLoadingHardwareForm,
  refetch: refetchHardwareForm,
  error: errorHardwareForm,
} = useHardwareForm(projectId)

// functions
async function refetchAll() {
  await refetchRegistration()
  await refetchHardwareForm()
  await refetchProject()
}

// derived states
const isOnflexPartner = computed(() =>
  project?.value?.is_onflex_partner
  || false,
)

const hasEnergyBillAnalysis = computed(() =>
  project?.value?.has_energy_bill_analysis
  || false,
)

const isLoading = computed(() =>
  isLoadingRegistration.value
  || isLoadingProject.value
  || isLoadingFinancing.value,
)
const isError = computed(() =>
  isErrorRegistration.value
  || isErrorProject.value
  || isErrorFinancing.value,
)
const error = computed(() => getErrorMessage([
  errorFinancing.value,
  errorHardwareForm.value,
  errorProject.value,
  errorRegistration.value,

]))

function navigateToLegacyProject(id: string | number) {
  window.location.href = `${env.VITE_URL_LEGACY}/financiamento/etapas/${id}`
}

function navigateToLegacyProjectList() {
  window.location.href = `${env.VITE_URL_LEGACY}/financiamentos`
}

onBeforeMount(() => {
  if (financing.value?.step === 'simulation' && financing.value?.status === 'reproved') {
    return router.push(`/simulation/result/${financing.value?.project_id}`)
  }
})

watch(financing, async (newFinancing) => {
  if (newFinancing && !isLoadingFinancing.value) {
    await nextTick()
    await validateFormalizationStep(newFinancing, projectId.value, route.path, router)
  }
}, { immediate: true })

// watches
watch([project, financing], ([newProjectState, newFinancingState]) => {
  if (newProjectState && newFinancingState && newProjectState?.new_journey === false) {
    setTimeout(() => {
      if (newFinancingState.legacy_id) {
        navigateToLegacyProject(newFinancingState?.legacy_id)
      }
      else {
        navigateToLegacyProjectList()
      }
    }, 2_000)
  }
}, { immediate: true })

watch(registration, (newState) => {
  if (newState?.person_type === 'pj') {
    router.push(`/company/${projectId.value}`)
  }
}, { immediate: true })
</script>

<template>
  <!-- loading state -->
  <div v-if="isLoading" class="container">
    <div class="my-4 card-container flex flex-col justify-between w-full">
      <span class="loader-project" />
    </div>
  </div>

  <div v-else class="container">
    <!-- error state -->
    <div v-if="isError">
      <SharedErrorsCheckAccess :error-message="error" />
    </div>

    <!-- success state -->
    <div v-else class="w-full">
      <!-- legacy project -->
      <div v-if="project?.new_journey === false" class="justify-center items-center flex flex-col bg-fff text-center h-[80vh] w-full">
        <SolEmptyState
          id="legacy-project-lock"
          title="Todos os projetos iniciados na antiga plataforma serão concluídos na mesma. "
          subtitle="Aguarde enquanto você está sendo redirecionado para antiga plataforma. Caso não seja redirecionado automaticamente, clique no botão abaixo."
          variant="empty-list"
          class="!max-w-full !md:system:max-w-[750px]"
        />
        <SolButton id="redirect" class="mt-4" @click="() => navigateToLegacyProject(financing?.legacy_id!)">
          Seguir para antiga plataforma
        </SolButton>
      </div>

      <!-- new journey project -->
      <div v-else class="w-full">
        <div v-if="registration" class="w-full">
          <Solfacil
            :registration="registration"
            :trigger-refetch="refetchAll"
            :is-onflex-partner="isOnflexPartner"
            :has-energy-bill-analysis="hasEnergyBillAnalysis"
            :system-power="project?.system_power"
            :is-turbo-prime="project?.is_turbo_prime === true"
            :hardware-form="{
              response: hardwareForm,
              refetch: refetchHardwareForm,
              isLoading: isLoadingHardwareForm,
              isError: isErrorHardwareForm,
            }"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Floating Widget -->
  <SimulationResumeWidget />
</template>

<style lang="scss">
.container {
  @apply flex flex-col py-2xs px-4xs gap-4 w-full;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }
}

.loader-project {
  @apply h-96 rounded-lg;
  @apply top-0 right-0 bottom-0 left-0 z-50;
  background: rgba(#fff, 0.9) url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat center center;
}
</style>
